import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/lib/shadcn/ui/dialog";
import { Button } from "@/lib/shadcn/ui/button";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/lib/shadcn/ui/select";
import { cn } from "@/lib/shadcn/utils";
import { dateToString } from "@/utils/periods";
import { formatDateToISODate, parseIsoDateOnly } from "@/utils/dates";
import { serverRejectionMessage } from "@/utils/errors";
import {
    BusinessFee,
    useAddBusinessFeeMutation,
    useGetApartmentBusinessFeesQuery,
    useRemoveBusinessFeeMutation,
    useUpdateBusinessFeeMutation,
} from "@/store/slices/apartments";

const MONTHS = [
    { value: 0, label: "January" },
    { value: 1, label: "February" },
    { value: 2, label: "March" },
    { value: 3, label: "April" },
    { value: 4, label: "May" },
    { value: 5, label: "June" },
    { value: 6, label: "July" },
    { value: 7, label: "August" },
    { value: 8, label: "September" },
    { value: 9, label: "October" },
    { value: 10, label: "November" },
    { value: 11, label: "December" },
];

/**
 * How long the dialog keeps watching for the removal of the control it restored
 * focus to. Long enough to cover the refetch a mutation triggers, short enough
 * that an unrelated later re-render cannot be mistaken for it.
 */
const TRIGGER_REMOVAL_WATCH_MS = 5000;

/** manual-business-fee/FR-004 — no fee may take effect before this date. */
const FEE_START_YEAR = 2026;
const FEE_START_DATE = new Date(FEE_START_YEAR, 0, 1);

/**
 * Only years a fee may legally take effect in are offered — the list starts at
 * the fee start year, so "earlier than 2026-01-01" cannot be picked. Extends two
 * years past the current one so a fee can be scheduled ahead.
 */
const selectableYears = (): number[] => {
    const last = Math.max(new Date().getFullYear() + 2, FEE_START_YEAR);
    return Array.from({ length: last - FEE_START_YEAR + 1 }, (_, index) => FEE_START_YEAR + index);
};

export interface EditBusinessFeeDialogProps {
    open: boolean;
    onClose: () => void;
    apartmentId: number;
    apartmentName: string;
    /**
     * The month to open on, as an ISO date. Defaults to the current month when
     * absent — right for editing a fee from its grid row, wrong for the readiness
     * banner, which knows the apartment has a gap in a specific EARLIER month
     * (`manual-business-fee/FR-020`). Defaulting to the current month there would
     * offer to fill a month that is already covered: the operator saves, nothing
     * changes and the notice never clears.
     */
    initialEffectiveFrom?: string;
    /**
     * Where focus goes when the control that opened the dialog no longer exists
     * by the time it closes — which is the NORMAL case here, not an edge one: the
     * mutations invalidate the data the banner and the grid are drawn from, so
     * saving from a banner entry or deleting from a grid row destroys the very
     * element Radix would otherwise restore focus to. Without a fallback, focus
     * silently falls to `<body>`.
     */
    restoreFocusRef?: React.RefObject<HTMLElement | null>;
}

const formatEuros = (amount: number | string): string => `€${Number(amount).toFixed(2)}`;

/**
 * The entry a submission for `effectiveFrom` would act on. Compares the ISO
 * date-only strings rather than parsed Dates for the timezone reason above; an
 * entry found here makes the submission an update, not a duplicate add (EC-006).
 */
function entryEffectiveOn(fees: BusinessFee[], effectiveFrom: string): BusinessFee | undefined {
    return fees.find((fee) => (fee.effectiveFrom ?? "").slice(0, 10) === effectiveFrom);
}

/** Most recent effective date first (manual-business-fee/AS-013). */
function mostRecentFirst(fees: BusinessFee[]): BusinessFee[] {
    return [...fees].sort((left, right) => (right.effectiveFrom ?? "").localeCompare(left.effectiveFrom ?? ""));
}

/**
 * The largest amount the `amount` column stores (NUMERIC(8,2)), mirroring
 * `businessfees.maxAmount`. Mirrored so the operator is told immediately rather
 * than after a round trip — NOT as the authority: a rule that exists only on the
 * server still reaches them, because a 400's message is surfaced verbatim (see
 * `serverRejectionMessage`).
 */
const MAX_AMOUNT = 999999.99;

/**
 * Mirrors the amount rules the service enforces, with the service's own messages
 * (`businessfees.ErrNegativeAmount`, `ErrTooManyDecimalPlaces`,
 * `ErrAmountTooLarge`). Validated from the RAW string because a rounded number
 * cannot tell "25.12" from "25.123" — EC-014 rejects the finer amount rather than
 * rounding it.
 */
function amountRejection(raw: string): string | undefined {
    const trimmed = raw.trim();
    if (trimmed === "") {
        return "business fee amount is required";
    }
    if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
        return "business fee amount must be a number";
    }
    if (Number(trimmed) < 0) {
        return "business fee amount must not be negative";
    }
    const decimals = trimmed.split(".")[1] ?? "";
    if (decimals.length > 2) {
        return "business fee amount must have at most two decimal places";
    }
    if (Number(trimmed) > MAX_AMOUNT) {
        return `business fee amount must not exceed ${MAX_AMOUNT.toFixed(2)}`;
    }
    return undefined;
}

/**
 * Mirrors `businessfees.ErrEffectiveFromBeforeStartDate`. Unreachable through
 * the picker as it stands — the year list starts at the fee start year and the
 * day is always the 1st — and kept as a guard so widening either list surfaces
 * the rule here rather than as a 400 from the server.
 */
function effectiveFromRejection(effectiveFrom: Date): string | undefined {
    if (effectiveFrom < FEE_START_DATE) {
        return `business fee effective date must not be earlier than ${formatDateToISODate(FEE_START_DATE)}`;
    }
    return undefined;
}

/**
 * A rejection carries the control it is ABOUT, not just its text. The client-side
 * rules render into one message element, and a message wired to the amount field
 * that actually talks about the effective date would be read out to a user
 * standing on the wrong control.
 *
 * A rejection the SERVER made is `"form"`: it arrives as prose, and picking a
 * field by searching that prose for "amount" would attach the message to a
 * control on the strength of a word — quietly mis-attributing every rule whose
 * wording is not what this file guessed. Form-level is the honest answer when the
 * server did not say which field, and the message still names the rule.
 */
interface Rejection {
    field: "amount" | "effectiveFrom" | "form";
    message: string;
}

function rejectionOf(rawAmount: string, effectiveFrom: Date): Rejection | undefined {
    const amount = amountRejection(rawAmount);
    if (amount) {
        return { field: "amount", message: amount };
    }
    const date = effectiveFromRejection(effectiveFrom);
    if (date) {
        return { field: "effectiveFrom", message: date };
    }
    return undefined;
}

/**
 * Add/edit dialog for a per-apartment, effective-dated monthly business fee.
 * Modelled on `EditCleaningRateDialog`, with two deliberate differences: the
 * effective date is a month/year pair rather than a free calendar day (FR-003
 * permits only the first of a month), and the apartment's history is RENDERED
 * (AS-013) rather than fetched purely as an add-vs-update lookup.
 */
export const EditBusinessFeeDialog: React.FC<EditBusinessFeeDialogProps> = ({
    open,
    onClose,
    apartmentId,
    apartmentName,
    initialEffectiveFrom,
    restoreFocusRef,
}) => {
    const [amount, setAmount] = useState<string>("");
    const [selectedMonth, setSelectedMonth] = useState<number>(() => new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState<number>(() => new Date().getFullYear());
    const [submitRejection, setSubmitRejection] = useState<Rejection | undefined>(undefined);

    const amountId = useId();
    const monthId = useId();
    const yearId = useId();
    const effectiveDateGroupId = useId();
    const rejectionId = useId();
    const historyHeadingId = useId();

    // The dialog owns the history request: it is what the add-vs-update decision
    // and the rendered history are both read from.
    const { data: fees, isFetching } = useGetApartmentBusinessFeesQuery(apartmentId, { skip: !open });
    const [addBusinessFee, { isLoading: isAdding }] = useAddBusinessFeeMutation();
    const [updateBusinessFee, { isLoading: isUpdating }] = useUpdateBusinessFeeMutation();
    const [removeBusinessFee, { isLoading: isRemoving }] = useRemoveBusinessFeeMutation();

    const history = useMemo(() => mostRecentFirst(fees ?? []), [fees]);

    // The first of the selected month — the only day a fee may take effect
    // (FR-003), which is why the day is derived rather than picked.
    const effectiveFromDate = useMemo(
        () => new Date(selectedYear, selectedMonth, 1),
        [selectedYear, selectedMonth],
    );
    const effectiveFrom = formatDateToISODate(effectiveFromDate);

    const existingEntry = entryEffectiveOn(history, effectiveFrom);
    const existingEntryId = existingEntry?.id;
    const existingEntryAmount = existingEntry?.amount;

    // The control the dialog was opened FROM, whether the close followed a
    // mutation, and the observer watching for that control's removal. Refs rather
    // than state: nothing renders from them, and they are read from Radix's close
    // callback after the last render.
    const triggerRef = useRef<Element | null>(null);
    const mutatedRef = useRef(false);
    const removalWatchRef = useRef<{ observer: MutationObserver; timeout: number } | null>(null);

    const stopWatchingForTriggerRemoval = useCallback(() => {
        const watch = removalWatchRef.current;
        if (!watch) {
            return;
        }
        watch.observer.disconnect();
        window.clearTimeout(watch.timeout);
        removalWatchRef.current = null;
    }, []);

    // Nothing may outlive the dialog: an observer left attached to the document
    // would keep firing, and a fallback focus after unmount would be a jump the
    // operator did not ask for.
    useEffect(() => stopWatchingForTriggerRemoval, [stopWatchingForTriggerRemoval]);

    // Reset to a clean add for the month the caller named — or the current month
    // when it named none — whenever the dialog is opened. A previous (possibly
    // rejected) attempt must not leak into a fresh one.
    useEffect(() => {
        if (open) {
            triggerRef.current = document.activeElement;
            mutatedRef.current = false;
            const openOn = parseIsoDateOnly(initialEffectiveFrom) ?? new Date();
            setAmount("");
            setSelectedMonth(openOn.getMonth());
            setSelectedYear(openOn.getFullYear());
            setSubmitRejection(undefined);
        }
    }, [open, apartmentId, initialEffectiveFrom]);

    // Picking a month that is already configured pre-fills its amount, so the
    // operator edits the entry rather than retyping it. Deliberately one-way:
    // moving to an unconfigured month keeps whatever is typed.
    useEffect(() => {
        if (existingEntryId !== undefined && existingEntryAmount !== undefined) {
            setAmount(Number(existingEntryAmount).toFixed(2));
        }
    }, [existingEntryId, existingEntryAmount]);

    const isBusy = isAdding || isUpdating || isRemoving;

    // The single close path — Save, Delete, Cancel, Escape, backdrop and the X all
    // arrive here, so focus restoration has exactly one place to happen.
    const close = (afterMutation: boolean) => {
        mutatedRef.current = afterMutation;
        setSubmitRejection(undefined);
        onClose();
    };
    /** Cancel, Escape, the backdrop and the X. */
    const handleDismiss = () => close(false);
    /** Save and Delete — the paths that can destroy the triggering control. */
    const handleCloseAfterMutation = () => close(true);

    /**
     * Hands focus to `fallback` if `trigger` leaves the document while it still
     * holds it. Bounded by `TRIGGER_REMOVAL_WATCH_MS` so a trigger that survives
     * (the common case) costs one short-lived observer rather than one per dialog
     * open for the life of the page.
     */
    const watchForTriggerRemoval = (trigger: HTMLElement, fallback: HTMLElement) => {
        stopWatchingForTriggerRemoval();
        const observer = new MutationObserver(() => {
            if (document.contains(trigger)) {
                return;
            }
            stopWatchingForTriggerRemoval();
            // Only if the removal is what cost the operator their position: if
            // they have moved on in the meantime, stealing focus back would be
            // the worse bug.
            if (document.activeElement === null || document.activeElement === document.body) {
                fallback.focus();
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        const timeout = window.setTimeout(stopWatchingForTriggerRemoval, TRIGGER_REMOVAL_WATCH_MS);
        removalWatchRef.current = { observer, timeout };
    };

    /**
     * Radix restores focus to whatever was focused when the dialog opened. Here
     * that node is often gone, because every mutation invalidates the queries the
     * banner and the grid are drawn from — so focus follows the trigger while it
     * exists, and is handed to the page region only when it genuinely does not.
     *
     * The existence check cannot be made once, and this is measured rather than
     * assumed: `onCloseAutoFocus` fires on the frame after the dialog closes,
     * while the refetch the mutation triggered is still a round trip away. At
     * restore time a doomed banner entry is therefore STILL in the document, so
     * `document.contains` alone would focus a node about to be destroyed and drop
     * the operator on `<body>`. Hence: focus the trigger, then — after a mutation
     * — watch for its removal and hand over if it goes.
     *
     * The blanket alternative (always fall back after a mutation) is what this
     * replaces: it also loses precision the trigger DID keep, e.g. editing a fee
     * whose grid row survives, which drops the operator from a specific edit
     * button to a generic region.
     */
    const restoreFocus = (event: Event) => {
        const trigger = triggerRef.current;
        const fallback = restoreFocusRef?.current;
        if (trigger instanceof HTMLElement && document.contains(trigger)) {
            event.preventDefault();
            trigger.focus();
            if (mutatedRef.current && fallback) {
                watchForTriggerRemoval(trigger, fallback);
            }
            return;
        }
        if (!fallback) {
            return;
        }
        event.preventDefault();
        fallback.focus();
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        const rejection = rejectionOf(amount, effectiveFromDate);
        if (rejection) {
            setSubmitRejection(rejection);
            return;
        }
        setSubmitRejection(undefined);
        try {
            if (existingEntry) {
                await updateBusinessFee({
                    id: existingEntry.id,
                    apartmentId,
                    amount: Number(amount),
                    effectiveFrom,
                }).unwrap();
            } else {
                await addBusinessFee({ apartmentId, amount: Number(amount), effectiveFrom }).unwrap();
            }
            toast.success(`Business fee for ${apartmentName} saved, effective ${effectiveFrom}.`);
            handleCloseAfterMutation();
        } catch (error) {
            // A rejection is shown IN the form, where the operator can act on it,
            // and the dialog stays open with their input intact. Only a fault gets
            // the generic toast (FR-019).
            const rejected = serverRejectionMessage(error);
            if (rejected) {
                setSubmitRejection({ field: "form", message: rejected });
                return;
            }
            toast.error(`Failed to save the business fee for ${apartmentName}. Please try again.`);
        }
    };

    const handleDelete = async () => {
        if (!existingEntry) {
            return;
        }
        try {
            await removeBusinessFee({ apartmentId, id: existingEntry.id }).unwrap();
            toast.success(`Business fee for ${apartmentName} effective ${effectiveFrom} deleted.`);
            handleCloseAfterMutation();
        } catch (error) {
            const rejected = serverRejectionMessage(error);
            if (rejected) {
                setSubmitRejection({ field: "form", message: rejected });
                return;
            }
            toast.error(`Failed to delete the business fee for ${apartmentName}. Please try again.`);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && handleDismiss()}>
            <DialogContent
                className="sm:max-w-md"
                data-testid="edit-business-fee-dialog"
                onCloseAutoFocus={restoreFocus}
            >
                <DialogHeader>
                    <DialogTitle data-testid="edit-business-fee-dialog-title">{apartmentName}</DialogTitle>
                    <DialogDescription>
                        Monthly business fee, charged from the month it takes effect until a later entry replaces it.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <section className="space-y-2" aria-labelledby={historyHeadingId}>
                        <h3 id={historyHeadingId} className="text-sm font-medium">
                            Fee history
                        </h3>
                        {history.length === 0 ? (
                            <p className="text-muted-foreground text-sm">
                                {isFetching ? "Loading…" : "No business fee configured yet."}
                            </p>
                        ) : (
                            <ul className="divide-y rounded-md border">
                                {history.map((fee) => (
                                    <li
                                        key={fee.id}
                                        data-testid={`business-fee-history-entry-${fee.id}`}
                                        className="flex items-center justify-between px-3 py-2 text-sm"
                                    >
                                        <span className="font-medium">{formatEuros(fee.amount)}</span>
                                        <span className="text-muted-foreground">
                                            from {dateToString(parseIsoDateOnly(fee.effectiveFrom))}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    <div className="h-px bg-gray-200" />

                    <div className="space-y-4">
                        <h3 className="text-sm font-medium">
                            {existingEntry ? "Change this month's fee" : "New business fee"}
                        </h3>

                        <div className="space-y-2">
                            <Label htmlFor={amountId}>
                                Amount (€) <span aria-hidden="true">*</span>
                            </Label>
                            <Input
                                id={amountId}
                                data-testid="business-fee-amount"
                                // Text rather than `number`: the >2-decimals rule (EC-014) is
                                // read off the RAW string, and a number input reports an
                                // unparseable entry as "" — which would hide it. It also keeps
                                // native constraint validation from silently blocking submit
                                // before our own message can be shown.
                                type="text"
                                inputMode="decimal"
                                value={amount}
                                onChange={(event) => setAmount(event.target.value)}
                                className={cn(
                                    submitRejection?.field === "amount" &&
                                        "border-red-500 focus-visible:ring-red-500",
                                )}
                                aria-required="true"
                                // Only the field the rejection is ABOUT is marked
                                // invalid and described by it (see `Rejection`).
                                aria-invalid={submitRejection?.field === "amount"}
                                aria-describedby={submitRejection?.field === "amount" ? rejectionId : undefined}
                            />
                        </div>

                        <div className="space-y-2" role="group" aria-labelledby={effectiveDateGroupId}>
                            <Label id={effectiveDateGroupId}>Effective from (first of the month)</Label>
                            <div className="grid grid-cols-2 gap-2">
                                <Select
                                    value={selectedMonth.toString()}
                                    onValueChange={(value) => setSelectedMonth(parseInt(value))}
                                >
                                    <SelectTrigger
                                        id={monthId}
                                        aria-label="Effective from month"
                                        aria-invalid={submitRejection?.field === "effectiveFrom"}
                                        aria-describedby={
                                            submitRejection?.field === "effectiveFrom" ? rejectionId : undefined
                                        }
                                    >
                                        <SelectValue placeholder="Select month" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MONTHS.map((month) => (
                                            <SelectItem key={month.value} value={month.value.toString()}>
                                                {month.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>

                                <Select
                                    value={selectedYear.toString()}
                                    onValueChange={(value) => setSelectedYear(parseInt(value))}
                                >
                                    <SelectTrigger
                                        id={yearId}
                                        aria-label="Effective from year"
                                        aria-invalid={submitRejection?.field === "effectiveFrom"}
                                        aria-describedby={
                                            submitRejection?.field === "effectiveFrom" ? rejectionId : undefined
                                        }
                                    >
                                        <SelectValue placeholder="Select year" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {selectableYears().map((year) => (
                                            <SelectItem key={year} value={year.toString()}>
                                                {year}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {submitRejection && (
                            <p id={rejectionId} role="alert" className="text-destructive text-sm">
                                {submitRejection.message}
                            </p>
                        )}
                    </div>

                    <DialogFooter className="gap-2 sm:justify-between">
                        {existingEntry ? (
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={handleDelete}
                                disabled={isBusy}
                                // The visible word is "Delete"; the accessible name names WHICH
                                // entry, and contains the visible text (WCAG 2.5.3 label-in-name).
                                aria-label={`Delete the ${formatEuros(existingEntry.amount)} business fee effective ${dateToString(parseIsoDateOnly(existingEntry.effectiveFrom))}`}
                                className="text-destructive hover:text-destructive"
                            >
                                <Trash2 aria-hidden="true" />
                                Delete
                            </Button>
                        ) : (
                            <span />
                        )}
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" onClick={handleDismiss} disabled={isBusy}>
                                Cancel
                            </Button>
                            {/* Never disabled on invalid input: a natively disabled button
                                takes no focus, so its `aria-describedby` reason is never
                                announced. Submitting reveals the rejection in a live region. */}
                            <Button type="submit" variant="default" disabled={isBusy}>
                                Save
                            </Button>
                        </div>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default EditBusinessFeeDialog;
